import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Borders = () => {
  return (
    <div className="tutorial-container">
      {/* Page Title */}
      <h1 className="tutorial-title">🖌️ CSS Borders</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <code>border</code> property in CSS is used to define the style,
        width, and color of an element’s border. Borders help separate content
        or highlight specific areas of a webpage. You can control each side of
        the border individually, or use shorthand to set them all at once.
      </p>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock code={`selector { border: width style color; }`} />
        <p>
          The <code>border</code> shorthand takes three values:
          <br />• <strong>width</strong>: Thickness of the border (e.g.,{" "}
          <code>1px</code>, <code>5px</code>, or keywords like <code>thin</code>
          , <code>medium</code>, <code>thick</code>).
          <br />• <strong>style</strong>: Defines how the border looks (solid,
          dashed, dotted, etc.).
          <br />• <strong>color</strong>: Any valid CSS color value (name, HEX,
          RGB, HSL).
        </p>
      </div>

      {/* Border Styles */}
      <div className="tutorial-section">
        <h2 className="section-title">Border Styles</h2>
        <p>
          The <code>border-style</code> property defines how the border line is
          drawn. You can choose from several styles:
        </p>
        <CodeCopyBlock
          code={`div {
  border-style: solid | dashed | dotted | double | groove | ridge | inset | outset | none | hidden;
}`}
        />

        <div style={{ display: "grid", gap: "12px", marginTop: "1rem" }}>
          {[
            "solid",
            "dashed",
            "dotted",
            "double",
            "groove",
            "ridge",
            "inset",
            "outset",
            "none",
            "hidden",
          ].map((style, i) => (
            <div
              key={i}
              style={{
                border: `4px ${style} #15803d`,
                padding: "10px",
                borderRadius: "6px",
                background: "#f9f9f9",
              }}
            >
              border-style: <strong>{style}</strong>
            </div>
          ))}
        </div>
        <p style={{ marginTop: "0.5rem" }}>
          • <code>solid</code>: Continuous line <br />• <code>dashed</code>:
          Broken into dashes <br />• <code>dotted</code>: Small dots <br />•{" "}
          <code>double</code>: Two parallel lines <br />• <code>groove</code>,{" "}
          <code>ridge</code>, <code>inset</code>, <code>outset</code>: 3D
          effects <br />• <code>none</code> / <code>hidden</code>: No border
        </p>
      </div>

      {/* Border Width and Color */}
      <div className="tutorial-section">
        <h2 className="section-title">Border Width and Color</h2>
        <p>
          The <code>border-width</code> property sets how thick the border is,
          and <code>border-color</code> changes its color. You must also specify{" "}
          <code>border-style</code> for the border to appear.
        </p>
        <CodeCopyBlock
          code={`div {
  border-width: 5px;
  border-color: red;
  border-style: solid;
}`}
        />
        <div
          style={{
            border: "5px solid red",
            padding: "15px",
            borderRadius: "6px",
            marginTop: "0.5rem",
          }}
        >
          🔴 5px solid red border
        </div>
      </div>

      {/* Shorthand */}
      <div className="tutorial-section">
        <h2 className="section-title">Border Shorthand</h2>
        <p>
          Instead of writing <code>border-width</code>,{" "}
          <code>border-style</code>, and <code>border-color</code> separately,
          you can combine them in one shorthand property.
        </p>
        <CodeCopyBlock
          code={`div {
  border: 3px dashed blue;
}`}
        />
        <div
          style={{
            border: "3px dashed blue",
            padding: "15px",
            borderRadius: "6px",
            marginTop: "0.5rem",
          }}
        >
          🔵 Shorthand: 3px dashed blue
        </div>
      </div>

      {/* Border Radius */}
      <div className="tutorial-section">
        <h2 className="section-title">Rounded Borders (Border-Radius)</h2>
        <p>
          The <code>border-radius</code> property rounds the corners of an
          element. You can use px, %, or em values to control the curve.
        </p>
        <CodeCopyBlock
          code={`div {
  border: 2px solid green;
  border-radius: 12px;
}`}
        />
        <div
          style={{
            border: "2px solid green",
            borderRadius: "12px",
            padding: "20px",
            marginTop: "0.5rem",
            background: "#f0fff4",
          }}
        >
          ✅ Rounded border with <code>border-radius</code>
        </div>
      </div>

      {/* Circle Example */}
      <div className="tutorial-section">
        <h2 className="section-title">Circle with Border</h2>
        <p>
          By using <code>border-radius: 50%</code> on a square element, you can
          create perfect circles. This is often used for profile pictures or
          buttons.
        </p>
        <CodeCopyBlock
          code={`div {
  border: 4px solid purple;
  border-radius: 50%;
  width: 100px;
  height: 100px;
}`}
        />
        <div
          style={{
            border: "4px solid purple",
            borderRadius: "50%",
            width: "100px",
            height: "100px",
            marginTop: "0.5rem",
            background: "lavender",
          }}
        />
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Borders can also be styled individually for each side using{" "}
          <code>border-top</code>, <code>border-right</code>,{" "}
          <code>border-bottom</code>, and <code>border-left</code>. You can even
          make creative shapes with <code>border-radius</code>. Next, check out{" "}
          <a href="/css/margins" className="learn-more">
            CSS Margins
          </a>{" "}
          to learn spacing outside elements.
        </p>
      </div>
    </div>
  );
};

export default CSS_Borders;
